import { useRef, useState } from 'react';
import { Modifier } from '@dnd-kit/abstract';
import { PointerActivationConstraints } from '@dnd-kit/dom';
import { arrayMove } from '@dnd-kit/helpers';
import { KeyboardSensor, PointerSensor } from '@dnd-kit/react';
import { isSortable } from '@dnd-kit/react/sortable';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { DATA_ATTR_DRAGGING } from '../constants.js';
function isGroupedOptions(options) {
    return !Array.isArray(options.items);
}
const SENSORS = [
    PointerSensor.configure({
        activationConstraints(event) {
            if (event.pointerType === 'touch') {
                // Slightly more forgiving than the lib's default 250ms/5px so finger jitter during the
                // hold doesn't abort activation. Per the docs (PointerSensor → Touch and mobile section).
                return [new PointerActivationConstraints.Delay({ value: 150, tolerance: 10 })];
            }
            return [
                new PointerActivationConstraints.Delay({ value: 200, tolerance: 10 }),
                new PointerActivationConstraints.Distance({ value: 5 }),
            ];
        },
    }),
    KeyboardSensor,
];
class RestrictToVerticalAxis extends Modifier {
    apply({ transform }) {
        return { x: 0, y: transform.y };
    }
}
const MODIFIERS = [RestrictToVerticalAxis];
export function useDnd(options) {
    const tableRef = useRef(null);
    const [overlayLayout, setOverlayLayout] = useState({ gridColumns: '', scrollLeft: 0, viewportWidth: 0 });
    const savedCursorRef = useRef('');
    const handleDragStart = useCallbackRef(() => {
        const grid = tableRef.current;
        if (grid) {
            // The TableScrollableWrapper is grid.parentElement and owns horizontal overflow.
            // Snapshot scroll position + visible width so the overlay can clip to the
            // viewport and align its inner grid with the columns currently in view.
            const scrollWrapper = grid.parentElement;
            setOverlayLayout({
                gridColumns: getComputedStyle(grid).gridTemplateColumns,
                scrollLeft: scrollWrapper?.scrollLeft ?? 0,
                viewportWidth: scrollWrapper?.clientWidth ?? 0,
            });
            // Lock horizontal scroll for the duration of the drag so the snapshot above stays valid
            // (scrolling sideways mid-drag would otherwise desync the overlay from the viewport).
            scrollWrapper?.setAttribute(DATA_ATTR_DRAGGING, 'true');
        }
        savedCursorRef.current = document.body.style.cursor;
        document.body.style.cursor = 'grabbing';
    });
    // Each group is its own sortable scope (rows can only reorder within their own group), so a drag never moves
    // a row to a different React parent — @dnd-kit reorders the rows live during the drag (and reverts on cancel),
    // and committing the final order on drag-end is enough. `index` is the group-relative slot the row was moved to.
    const handleDragEnd = useCallbackRef(({ operation, canceled }) => {
        document.body.style.cursor = savedCursorRef.current;
        tableRef.current?.parentElement?.removeAttribute(DATA_ATTR_DRAGGING);
        if (canceled)
            return;
        const { source } = operation;
        if (!isSortable(source))
            return;
        const { initialIndex, index, group } = source.sortable;
        if (initialIndex === index)
            return;
        if (isGroupedOptions(options)) {
            if (group == null)
                return;
            const slice = options.items[group];
            if (!slice)
                return;
            const next = { ...options.items, [group]: arrayMove(slice, initialIndex, index) };
            options.onReorder(next);
            options.onDragEnd?.(next);
            return;
        }
        const next = arrayMove(options.items, initialIndex, index);
        options.onReorder(next);
        options.onDragEnd?.(next);
    });
    return {
        tableRef: tableRef,
        providerProps: {
            sensors: SENSORS,
            modifiers: MODIFIERS,
            onDragStart: handleDragStart,
            onDragEnd: handleDragEnd,
        },
        overlayProps: overlayLayout,
    };
}
